import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  FormControlLabel,
  Checkbox,
  Paper
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Block as BlockIcon,
  LocalShipping as ShippingIcon,
  EventAvailable as AvailableIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  ViewModule as MonthViewIcon,
  ViewWeek as WeekViewIcon,
  ViewDay as DayViewIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon
} from '@mui/icons-material';
import { DateCalendar, LocalizationProvider, PickersDay, DayCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useOrg } from '../../contexts/OrgContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const EPLCalendar = () => {
  const { org } = useOrg();
  const effectiveOrg = (org || 'epl').toLowerCase();

  const [viewType, setViewType] = useState('month'); // 'month', 'week', 'day'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDatesForBlocking, setSelectedDatesForBlocking] = useState([]); // Array of dates for multi-select
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [shippingDays, setShippingDays] = useState({
    monday: true,
    tuesday: true,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Dialog states
  const [blockDateDialogOpen, setBlockDateDialogOpen] = useState(false);
  const [blockDateForm, setBlockDateForm] = useState({
    dates: [], // Changed to array for multi-date support
    reason: '',
    type: 'BLOCKED' // BLOCKED, HOLIDAY, NO_SHIPPING
  });

  // Fetch calendar settings
  const fetchCalendarSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/calendar/settings?organization=EPL`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBlockedDates(data.data.blockedDates || []);
          setShippingDays(data.data.shippingDays || shippingDays);
        }
      } else if (response.status === 404) {
        // No settings yet, use defaults
        setBlockedDates([]);
      }
    } catch (err) {
      console.error('Error fetching calendar settings:', err);
      setError('Failed to load calendar settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendarSettings();
  }, [fetchCalendarSettings]);

  // Helper function to normalize date to start of day - handles timezone issues
  const normalizeDate = (date) => {
    let d;
    if (date instanceof Date) {
      d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    } else if (typeof date === 'string') {
      // If it's an ISO string, parse just the date part to avoid timezone issues
      if (date.includes('T') || date.includes('Z')) {
        const datePart = date.split('T')[0];
        const [year, month, day] = datePart.split('-');
        d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else if (date.includes('-')) {
        const [year, month, day] = date.split('-');
        d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        d = new Date(date);
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      }
    } else {
      d = new Date(date);
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return d;
  };

  // Helper function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    const d1 = normalizeDate(date1);
    const d2 = normalizeDate(date2);
    return d1.getTime() === d2.getTime();
  };

  // Helper function to format date - preserves local date without timezone shift
  const formatDate = (date, formatStr = 'yyyy-MM-dd') => {
    // If it's already a Date object, use it directly
    // If it's a string, parse it carefully
    let d;
    if (date instanceof Date) {
      d = date;
    } else if (typeof date === 'string') {
      // If it's an ISO string, parse just the date part to avoid timezone issues
      if (date.includes('T') || date.includes('Z')) {
        const datePart = date.split('T')[0];
        const [year, month, day] = datePart.split('-');
        d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        const [year, month, day] = date.split('-');
        d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    } else {
      d = new Date(date);
    }
    
    if (formatStr === 'yyyy-MM-dd') {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return d.toLocaleDateString();
  };

  // Check if a date is blocked - compare using ISO date strings
  const isDateBlocked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.some(blocked => blocked.date === dateStr);
  };

  // Get blocked date info
  const getBlockedDateInfo = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.find(blocked => blocked.date === dateStr);
  };

  // Check if a date is a shipping day
  const isShippingDay = (date) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[date.getDay()];
    return shippingDays[dayOfWeek] || false;
  };

  // Check if a date is selected for blocking - uses ISO string for consistency
  const isSelectedForBlocking = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return selectedDatesForBlocking.includes(dateStr);
  };

  // Toggle date selection for blocking - stores as string to avoid timezone issues
  const toggleDateSelection = (date) => {
    if (isDateBlocked(date)) {
      // Don't allow selecting already blocked dates
      return;
    }
    
    // Use ISO string which is always UTC and consistent
    const dateStr = date.toISOString().split('T')[0];
    
    setSelectedDatesForBlocking(prev => {
      const isAlreadySelected = prev.includes(dateStr);
      if (isAlreadySelected) {
        return prev.filter(d => d !== dateStr);
      } else {
        return [...prev, dateStr];
      }
    });
  };

  // Clear all selected dates
  const clearSelectedDates = () => {
    setSelectedDatesForBlocking([]);
    setIsMultiSelectMode(false);
  };

  // Handle blocking multiple dates
  const handleBlockDates = async () => {
    if (blockDateForm.dates.length === 0) {
      setError('Please select at least one date');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    const token = localStorage.getItem('authToken');
    let successCount = 0;
    let failCount = 0;

    // Block each date - dates are already stored as strings like "2025-12-24"
    for (const dateStr of blockDateForm.dates) {
      try {
        const response = await fetch(`${API_BASE_URL}/calendar/block-date`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            date: dateStr, // Already a string, no conversion needed
            reason: blockDateForm.reason || null,
            type: blockDateForm.type,
            organization: 'EPL'
          })
        });
        
        const data = await response.json();
        if (data.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        console.error('Error blocking date:', err);
        failCount++;
      }
    }

    if (successCount > 0) {
      setSuccessMessage(`Successfully blocked ${successCount} date${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`);
      setBlockDateDialogOpen(false);
      setBlockDateForm({ dates: [], reason: '', type: 'BLOCKED' });
      setSelectedDatesForBlocking([]);
      setIsMultiSelectMode(false);
      fetchCalendarSettings();
    } else {
      setError('Failed to block dates');
    }
    
    setSaving(false);
  };

  // Handle unblocking a date
  const handleUnblockDate = async (dateId) => {
    if (!window.confirm('Are you sure you want to unblock this date?')) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/calendar/blocked-dates/${dateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('Date unblocked successfully');
        fetchCalendarSettings();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to unblock date');
      }
    } catch (err) {
      console.error('Error unblocking date:', err);
      setError('Failed to unblock date');
    } finally {
      setSaving(false);
    }
  };

  // Handle saving shipping days
  const handleSaveShippingDays = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/calendar/shipping-days`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          shippingDays,
          organization: 'EPL'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuccessMessage('Shipping days updated successfully');
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update shipping days');
      }
    } catch (err) {
      console.error('Error saving shipping days:', err);
      setError('Failed to save shipping days');
    } finally {
      setSaving(false);
    }
  };

  // Handle special override for specific days (e.g., Thursday for special occasions)
  const handleSpecialOverride = async (day, enabled, reason = '') => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/calendar/shipping-days/special-override`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          organization: 'EPL',
          day,
          enabled,
          reason: reason || `Administrative override for ${day} shipping`
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuccessMessage(`${day.charAt(0).toUpperCase() + day.slice(1)} shipping ${enabled ? 'enabled' : 'disabled'} successfully`);
          // Update local state with the response data
          setShippingDays(data.data);
        }
      } else {
        const data = await response.json();
        setError(data.message || `Failed to ${enabled ? 'enable' : 'disable'} ${day} shipping`);
        // Revert local state on error
        setShippingDays(prev => ({
          ...prev,
          [day]: !enabled
        }));
      }
    } catch (err) {
      console.error(`Error ${enabled ? 'enabling' : 'disabling'} ${day} shipping:`, err);
      setError(`Failed to ${enabled ? 'enable' : 'disable'} ${day} shipping`);
      // Revert local state on error
      setShippingDays(prev => ({
        ...prev,
        [day]: !enabled
      }));
    } finally {
      setSaving(false);
    }
  };

  // Navigation functions
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (viewType === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Get week start date (Sunday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  };

  // Get week dates
  const getWeekDates = (date) => {
    const start = getWeekStart(date);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  // Format date for display
  const getViewTitle = () => {
    if (viewType === 'month') {
      return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewType === 'week') {
      const weekStart = getWeekStart(selectedDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  // Custom day renderer for calendar
  const CustomDay = (props) => {
    const { day, ...other } = props;
    const isBlocked = isDateBlocked(day);
    const isShipping = isShippingDay(day);
    const isSelected = isSelectedForBlocking(day);
    const blockedInfo = isBlocked ? getBlockedDateInfo(day) : null;

    const handleDayClick = (e) => {
      if (isMultiSelectMode) {
        e.preventDefault();
        e.stopPropagation();
        toggleDateSelection(day);
      }
    };

    return (
      <Tooltip
        title={
          isSelected
            ? 'Click to deselect'
            : isBlocked
            ? `Blocked: ${blockedInfo?.reason || blockedInfo?.type || 'No shipping'}`
            : isMultiSelectMode
            ? 'Click to select for blocking'
            : isShipping
            ? 'Shipping day'
            : 'No shipping'
        }
        arrow
      >
        <PickersDay
          {...other}
          day={day}
          onClick={isMultiSelectMode ? handleDayClick : other.onClick}
          sx={{
            ...(isSelected && {
              bgcolor: 'rgba(33, 150, 243, 0.3)',
              border: '2px solid #2196f3',
              '&:hover': {
                bgcolor: 'rgba(33, 150, 243, 0.4)',
              },
            }),
            ...(isBlocked && !isSelected && {
              bgcolor: 'rgba(244, 67, 54, 0.2)',
              border: '2px solid #f44336',
              '&:hover': {
                bgcolor: 'rgba(244, 67, 54, 0.3)',
              },
            }),
            ...(!isShipping && !isBlocked && !isSelected && {
              bgcolor: 'rgba(0, 0, 0, 0.05)',
              color: 'text.disabled',
            }),
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box>
      <Breadcrumbs 
        items={[
          { label: 'EPL Internal', path: `/internal/epl/dashboard` },
          { label: 'EPL Calendar' }
        ]} 
      />

      {/* Header Banner */}
      <Box sx={{
        mb: 4,
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        boxShadow: '0 4px 12px rgba(3, 149, 130, 0.3)'
      }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            EPL Calendar
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Manage shipping schedules and blocked dates
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {isMultiSelectMode ? (
            <>
              <Button
                variant="contained"
                onClick={clearSelectedDates}
                sx={{
                  bgcolor: 'white',
                  color: '#039582',
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<BlockIcon />}
                onClick={() => {
                  setBlockDateForm({
                    dates: [...selectedDatesForBlocking],
                    reason: '',
                    type: 'BLOCKED'
                  });
                  setBlockDateDialogOpen(true);
                }}
                disabled={selectedDatesForBlocking.length === 0}
                sx={{
                  bgcolor: 'white',
                  color: '#039582',
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                    color: 'rgba(3, 149, 130, 0.6)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Block {selectedDatesForBlocking.length} Date{selectedDatesForBlocking.length !== 1 ? 's' : ''}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<CalendarIcon />}
                onClick={() => setIsMultiSelectMode(true)}
                sx={{
                  bgcolor: 'white',
                  color: '#039582',
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
                  },
                  transition: 'all 0.3s ease',
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Select Multiple Dates
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  // Use ISO string for consistency
                  const dateStr = selectedDate.toISOString().split('T')[0];
                  setBlockDateForm({
                    dates: [dateStr],
                    reason: '',
                    type: 'BLOCKED'
                  });
                  setBlockDateDialogOpen(true);
                }}
                sx={{
                  bgcolor: 'white',
                  color: '#039582',
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Block Date
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {isMultiSelectMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Multi-Select Mode:</strong> Click on dates in the calendar to select them for blocking. 
          {selectedDatesForBlocking.length > 0 && (
            <span> Selected: <strong>{selectedDatesForBlocking.length}</strong> date{selectedDatesForBlocking.length !== 1 ? 's' : ''}</span>
          )}
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Calendar */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {/* View Navigation */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 2, 
                flexWrap: 'wrap', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {getViewTitle()}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                  {/* View Type Selector */}
                  <ButtonGroup size="small" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <Button
                      variant={viewType === 'month' ? 'contained' : 'outlined'}
                      startIcon={<MonthViewIcon />}
                      onClick={() => setViewType('month')}
                    >
                      Month
                    </Button>
                    <Button
                      variant={viewType === 'week' ? 'contained' : 'outlined'}
                      startIcon={<WeekViewIcon />}
                      onClick={() => setViewType('week')}
                    >
                      Week
                    </Button>
                    <Button
                      variant={viewType === 'day' ? 'contained' : 'outlined'}
                      startIcon={<DayViewIcon />}
                      onClick={() => setViewType('day')}
                    >
                      Day
                    </Button>
                  </ButtonGroup>
                  {/* Mobile View Selector */}
                  <FormControl size="small" sx={{ minWidth: 100, display: { xs: 'block', sm: 'none' } }}>
                    <Select
                      value={viewType}
                      onChange={(e) => setViewType(e.target.value)}
                    >
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="week">Week</MenuItem>
                      <MenuItem value="day">Day</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Navigation Buttons */}
                  <IconButton onClick={() => navigateDate(-1)} size="small">
                    <ChevronLeftIcon />
                  </IconButton>
                  <Button
                    size="small"
                    startIcon={<TodayIcon />}
                    onClick={goToToday}
                    variant="outlined"
                  >
                    Today
                  </Button>
                  <IconButton onClick={() => navigateDate(1)} size="small">
                    <ChevronRightIcon />
                  </IconButton>
                </Stack>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                {viewType === 'month' && (
                  <DateCalendar
                    value={selectedDate}
                    onChange={(newDate) => setSelectedDate(newDate)}
                    slots={{
                      day: CustomDay,
                    }}
                    sx={{
                      '& .MuiPickersDay-root': {
                        '&.Mui-selected': {
                          bgcolor: '#039582',
                          '&:hover': {
                            bgcolor: '#027A6B'
                          }
                        }
                      }
                    }}
                  />
                )}

                {viewType === 'week' && (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Grid container spacing={{ xs: 0.5, sm: 1 }} sx={{ mb: 1 }}>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                        <Grid item xs key={index}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textAlign: 'center',
                              display: 'block',
                              color: index === 0 || index === 6 ? 'text.secondary' : 'text.primary',
                              fontSize: { xs: '0.7rem', sm: '0.75rem' }
                            }}
                          >
                            {day}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                    <Grid container spacing={{ xs: 0.5, sm: 1 }}>
                      {getWeekDates(selectedDate).map((date, index) => {
                        const isBlocked = isDateBlocked(date);
                        const isShipping = isShippingDay(date);
                        const isSelected = isSelectedForBlocking(date);
                        const blockedInfo = isBlocked ? getBlockedDateInfo(date) : null;
                        const isToday = isSameDay(date, new Date());
                        const isSelectedDate = isSameDay(date, selectedDate);

                        return (
                          <Grid item xs key={index}>
                            <Paper
                              sx={{
                                p: { xs: 0.75, sm: 1 },
                                minHeight: { xs: 70, sm: 100, md: 120 },
                                cursor: isMultiSelectMode ? 'pointer' : 'default',
                                border: isSelectedDate ? '2px solid #039582' : '1px solid',
                                borderColor: isToday ? 'primary.main' : 'divider',
                                bgcolor: isBlocked
                                  ? 'error.light'
                                  : isSelected
                                  ? 'primary.light'
                                  : isShipping
                                  ? 'success.light'
                                  : 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: isMultiSelectMode
                                    ? 'action.hover'
                                    : isBlocked
                                    ? 'error.light'
                                    : 'action.hover',
                                  transform: 'translateY(-2px)',
                                  boxShadow: 2
                                }
                              }}
                              onClick={() => {
                                if (isMultiSelectMode) {
                                  toggleDateSelection(date);
                                } else {
                                  setSelectedDate(date);
                                }
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: isToday ? 700 : 500,
                                  color: isToday ? 'primary.main' : 'text.primary',
                                  display: 'block',
                                  mb: 0.5,
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              >
                                {date.getDate()}
                              </Typography>
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                {isBlocked && (
                                  <Tooltip title={blockedInfo?.reason || blockedInfo?.type || 'Blocked'}>
                                    <BlockIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'error.main' }} />
                                  </Tooltip>
                                )}
                                {!isBlocked && isShipping && (
                                  <Tooltip title="Shipping day">
                                    <ShippingIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'success.main' }} />
                                  </Tooltip>
                                )}
                                {!isBlocked && !isShipping && (
                                  <Tooltip title="No shipping">
                                    <AvailableIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.disabled' }} />
                                  </Tooltip>
                                )}
                              </Stack>
                              {blockedInfo?.reason && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {blockedInfo.reason}
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {viewType === 'day' && (
                  <Box>
                    <Paper
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        minHeight: { xs: 200, sm: 300 },
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: isDateBlocked(selectedDate)
                          ? 'error.light'
                          : isShippingDay(selectedDate)
                          ? 'success.light'
                          : 'background.paper'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 600,
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        {selectedDate.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </Typography>
                      <Stack spacing={2}>
                        {isDateBlocked(selectedDate) && (
                          <Alert severity="error" icon={<BlockIcon />}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              Blocked Date
                            </Typography>
                            {getBlockedDateInfo(selectedDate)?.reason && (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {getBlockedDateInfo(selectedDate).reason}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={getBlockedDateInfo(selectedDate)?.type || 'BLOCKED'}
                                size="small"
                                color="error"
                              />
                            </Box>
                          </Alert>
                        )}
                        {!isDateBlocked(selectedDate) && isShippingDay(selectedDate) && (
                          <Alert severity="success" icon={<ShippingIcon />}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              Shipping Day Available
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              Orders can be shipped on this day.
                            </Typography>
                          </Alert>
                        )}
                        {!isDateBlocked(selectedDate) && !isShippingDay(selectedDate) && (
                          <Alert severity="info" icon={<AvailableIcon />}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              No Shipping
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              This is not a scheduled shipping day.
                            </Typography>
                          </Alert>
                        )}
                        {isMultiSelectMode && (
                          <Button
                            variant="outlined"
                            startIcon={<BlockIcon />}
                            onClick={() => {
                              const dateStr = selectedDate.toISOString().split('T')[0];
                              setBlockDateForm({
                                dates: [dateStr],
                                reason: '',
                                type: 'BLOCKED'
                              });
                              setBlockDateDialogOpen(true);
                            }}
                            fullWidth
                          >
                            Block This Date
                          </Button>
                        )}
                      </Stack>
                    </Paper>
                  </Box>
                )}
              </LocalizationProvider>
              
              <Divider sx={{ my: 2 }} />
              
              <Stack 
                direction="row" 
                spacing={{ xs: 1, sm: 2 }} 
                flexWrap="wrap" 
                gap={1}
                sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}
              >
                <Chip
                  icon={<BlockIcon />}
                  label="Blocked Date"
                  color="error"
                  size="small"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                />
                <Chip
                  icon={<ShippingIcon />}
                  label="Shipping Day"
                  color="success"
                  size="small"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                />
                <Chip
                  icon={<AvailableIcon />}
                  label="No Shipping"
                  color="default"
                  size="small"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                />
                {isMultiSelectMode && (
                  <Chip
                    icon={<CalendarIcon />}
                    label="Selected"
                    color="primary"
                    size="small"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Panel */}
        <Grid item xs={12} md={4} sx={{ order: { xs: -1, md: 0 } }}>
          <Stack spacing={3}>
            {/* Shipping Days */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Allowed Shipping Days
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure which days of the week are allowed for shipping. Default: Monday & Tuesday
                </Typography>
                <Stack spacing={1}>
                  {Object.entries(shippingDays).map(([day, enabled]) => {
                    const isDefault = day === 'monday' || day === 'tuesday';
                    const isSpecialOverride = day === 'thursday' && enabled;
                    return (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={enabled}
                            onChange={(e) => {
                              setShippingDays({
                                ...shippingDays,
                                [day]: e.target.checked
                              });
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </Typography>
                            {isDefault && (
                              <Chip 
                                label="Default" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            )}
                            {isSpecialOverride && (
                              <Chip 
                                label="Special Override" 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveShippingDays}
                  disabled={saving}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Save Shipping Days
                </Button>
                
                {/* Special Override Section */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Administrative Override
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enable special shipping days for occasions that require deviation from the standard Monday/Tuesday schedule.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    variant={shippingDays.thursday ? "contained" : "outlined"}
                    color={shippingDays.thursday ? "secondary" : "inherit"}
                    size="small"
                    onClick={() => {
                      const newEnabled = !shippingDays.thursday;
                      setShippingDays({
                        ...shippingDays,
                        thursday: newEnabled
                      });
                      // Auto-save the special override
                      handleSpecialOverride('thursday', newEnabled);
                    }}
                    disabled={saving}
                  >
                    {shippingDays.thursday ? 'Disable' : 'Enable'} Thursday Shipping
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Blocked Dates List */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Blocked Dates
                </Typography>
                {blockedDates.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No dates blocked
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {blockedDates
                      .sort((a, b) => a.date.localeCompare(b.date))
                      .map((blocked) => {
                        // Parse date string without timezone shift
                        const [year, month, day] = blocked.date.split('-').map(Number);
                        const blockedDate = new Date(year, month - 1, day);
                        const formattedDate = blockedDate.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: '2-digit', 
                          year: 'numeric' 
                        });
                        return (
                        <Paper
                          key={blocked.id}
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {formattedDate}
                            </Typography>
                            {blocked.reason && (
                              <Typography variant="caption" color="text.secondary">
                                {blocked.reason}
                              </Typography>
                            )}
                            <Chip
                              label={blocked.type}
                              size="small"
                              color={
                                blocked.type === 'HOLIDAY'
                                  ? 'warning'
                                  : blocked.type === 'NO_SHIPPING'
                                  ? 'info'
                                  : 'error'
                              }
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleUnblockDate(blocked.id)}
                            disabled={saving}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Paper>
                        );
                      })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Block Date Dialog */}
      <Dialog open={blockDateDialogOpen} onClose={() => setBlockDateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Block {blockDateForm.dates.length > 1 ? `${blockDateForm.dates.length} Dates` : 'Date'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Show selected dates */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Date{blockDateForm.dates.length > 1 ? 's' : ''}:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {blockDateForm.dates.map((dateStr, index) => {
                  // Parse date string "2025-12-24" properly without timezone shift
                  const [year, month, day] = dateStr.split('-').map(Number);
                  const displayDate = new Date(year, month - 1, day);
                  return (
                    <Chip
                      key={index}
                      label={displayDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric'
                      })}
                      onDelete={() => {
                        const newDates = blockDateForm.dates.filter((_, i) => i !== index);
                        setBlockDateForm({ ...blockDateForm, dates: newDates });
                      }}
                      color="primary"
                      size="small"
                    />
                  );
                })}
              </Stack>
            </Box>
            
            <FormControl fullWidth>
              <InputLabel>Block Type</InputLabel>
              <Select
                value={blockDateForm.type}
                onChange={(e) => setBlockDateForm({ ...blockDateForm, type: e.target.value })}
                label="Block Type"
              >
                <MenuItem value="BLOCKED">Blocked (No Orders)</MenuItem>
                <MenuItem value="HOLIDAY">Holiday</MenuItem>
                <MenuItem value="NO_SHIPPING">No Shipping</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Reason (Optional)"
              multiline
              rows={3}
              value={blockDateForm.reason}
              onChange={(e) => setBlockDateForm({ ...blockDateForm, reason: e.target.value })}
              placeholder="e.g., Company holiday, Maintenance day..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDateDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleBlockDates}
            variant="contained"
            disabled={saving || blockDateForm.dates.length === 0}
            startIcon={saving ? null : <SaveIcon />}
          >
            {saving ? 'Saving...' : `Block ${blockDateForm.dates.length} Date${blockDateForm.dates.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EPLCalendar;

